<template>
    <div class="tab-template-message-list">
        <div class="header">
            <div class="title">{{ $t("whatsapp_business_api.templates") }}</div>
            <div class="d-flex gap-5">
                <a-button type="primary" @click="gotoCreatePage" ghost>
                    <a-icon type="plus" />
                    {{ $t("whatsapp_business_api.createNewTemplate") }}
                </a-button>
                <a-button type="primary" @click="refreshTemplates" ghost>{{ $t("message.refresh") }}</a-button>
            </div>
        </div>

        <a-input-search
            v-model="searchQuery"
            :placeholder="$t('whatsapp_business_api.searchTemplate')"
            style="margin-bottom: 16px"
        />

        <div class="content">
            <div class="left">
                <div class="templates">
                    <TemplateCard
                        v-for="template of filteredTemplates"
                        :key="template.id"
                        :template="template"
                        :on-click="() => (selectedTemplate = template)"
                        @deleteTemplate="cbDeleteTemplate"
                        :selected="selectedTemplate && selectedTemplate.id === template.id"
                    />
                </div>
            </div>
            <div class="right">
                <div class="preview-container">
                    <WhatsappMessagePreview
                        v-if="selectedTemplate"
                        :template-type="getTemplateParameters(selectedTemplate).headerType"
                        :body="getTemplateParameters(selectedTemplate).body"
                        :footer="getTemplateParameters(selectedTemplate).footer"
                        :header-media-type="getTemplateParameters(selectedTemplate).headerType"
                        :header-text="getTemplateParameters(selectedTemplate).headerText"
                        :buttons="getTemplateParameters(selectedTemplate).buttons"
                        :headerMediaURL="getTemplateParameters(selectedTemplate).headerMediaURL"
                        :cards="getTemplateParameters(selectedTemplate).cards"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import _ from "lodash";
import log2 from "../../../../utils/log2";
import TemplateCard from "./TemplateCard.vue";
import WABusinessMenuTypes from "./WABusinessMenuTypes";
import { mapActions, mapState } from "vuex";
import WhatsappMessagePreview from "../WhatsappMessagePreview.vue";
import { sendSocketMessage, socketMessageStream } from "../../../../utils/socketStream";

const log = log2("TemplateMessageList.vue");
export default Vue.extend({
    name: "TemplateMessageList",
    components: { WhatsappMessagePreview, TemplateCard },
    props: {
        value: {
            required: true,
        },
        changePage: {
            type: Function,
            default: () => {},
        },
    },
    data() {
        return {
            clone: _.cloneDeep(this.value),
            selectedTemplate: null,
            subscriber: null,
            templates: [],
            searchQuery: "",
        };
    },
    computed: {
        ...mapState({
            socketConnected: (state) => state.socketStatus.socket_connected,
        }),
        filteredTemplates() {
            return this.templates.filter((template) =>
                template.elementName.toLowerCase().includes(this.searchQuery.toLowerCase())
            );
        },
    },
    watch: {
        clone: {
            handler: function (val) {
                if (val) {
                    const isChanged = !_.isEqual(val, this.value);
                    if (isChanged) {
                        log("clone changed=>", val);
                        this.$emit("input", val);
                    }
                }
            },
            deep: true,
        },
        value: {
            handler: function (val) {
                const isChanged = !_.isEqual(val, this.clone);
                if (isChanged) {
                    log("value changed=>", val);
                    this.clone = _.cloneDeep(val);
                }
            },
            deep: true,
        },
    },
    methods: {
        ...mapActions({
            getTemplates: "whatsapp/getTemplates",
            deleteTemplate: "whatsapp/deleteTemplate",
        }),
        getChannelTemplates() {
            this.getTemplates(this.value._id).then((templates) => {
                this.templates = templates;
            });
        },
        refreshTemplates() {
            sendSocketMessage("whatsapp refresh-template", {
                channelId: this.value._id,
                siteId: this.value.siteId,
            });
            this.subscriber = socketMessageStream.subscribe((message) => {
                if (message.cmd === "whatsapp_template_refreshed") {
                    this.getChannelTemplates();
                    this.subscriber.unsubscribe();
                }
            });
        },
        getTemplateParameters(template) {
            const metaObject = JSON.parse(template.meta);
            const containerMetaObj = JSON.parse(template.containerMeta);
            const headerMediaURL = (containerMetaObj.sampleMedia || "").startsWith("http")
                ? containerMetaObj.sampleMedia
                : template.asset && (template.asset.v2Format && template.asset.cdnUrl
                  ? template.asset.cdnUrl
                  : `https://static.supsis.live/${template.asset.path}${template.asset.id}.${template.asset.ext}`);

            return {
                headerMediaURL,
                headerType: template.templateType, //text|image,
                headerMediaType: template.templateType, //image|video|document|location,
                headerText: _.get(containerMetaObj, "header"),
                footer: _.get(containerMetaObj, "footer"),
                body: _.get(metaObject, "example") || metaObject.data,
                buttons: _.get(containerMetaObj, "buttons") || template.buttons,
                cards: _.get(containerMetaObj, "cards", []),
            };
        },
        gotoCreatePage() {
            this.changePage(WABusinessMenuTypes.MENU_TYPES.NEW_TEMPLATE);
        },
        cbDeleteTemplate(template) {
            this.deleteTemplate({
                channelId: this.value._id,
                templateName: template.elementName,
            }).then(() => {
                this.templates = this.templates.filter((t) => t.id !== template.id);
                this.$message.success(`${template.elementName} Template is Successfully Deleted.`);
            });
        },
    },
    mounted() {
        this.getChannelTemplates();
    },
});
</script>

<style scoped lang="scss">
.tab-template-message-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 10px;

        border-bottom: 1px solid var(--color-border-default);

        .title {
            font-size: 18px;
            font-weight: 700;
            color: var(--color-text-strong-default);
        }

        .subtitle {
            font-size: 14px;
            color: var(--color-text-strong-default);
        }
    }

    .content {
        display: flex;
        flex-wrap: wrap;
        gap: 20px;

        .left {
            width: calc(100% - 400px);
        }

        .right {
            display: flex;
            position: relative;

            .preview-container {
                position: sticky;
                top: 20px;
                max-height: 600px;
            }
        }
    }

    .templates {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
}
</style>
